import { useEffect, useState } from 'react'
import candidateChatManager from '../../candidate/CandidateChatManager'
import { applyCoachLoadBalancing } from '../../utils/applyCoachLoadBalancing'
import {
  filterCoachesByStatus,
  getLocaleCompanyFilteredCoaches,
} from '../shared/filterCoaches'

/**
 * RTC connection + coach occupancy for the video-frame coach picker.
 */
export function useCoachListConnection(connectionProps) {
  const {
    socketUrl,
    username,
    chatRoom,
    inHours,
    logChatEventPath,
    chatCoachInfoPath,
    serverTime,
    currentUserLocale,
    userDisplayName,
    userFirstName,
    videoEnabled,
    browserName,
    userLocale,
    userLanguage,
    userCompany,
    userCompanyId,
    partnerCandidate,
    conversationStarter,
    chatMode,
    userId,
    userEmail,
    avatar,
    os,
    buildChatHistoryPath,
    conversationId,
    isMobile,
    clientTime,
    timeOffset,
    deloitteUser,
    userCareerDevelopment,
    outplacement,
    opRedesign,
    chatHours,
    hideChatHours,
    modalCopy,
  } = connectionProps

  const [isConnected, setIsConnected] = useState(false)
  const [coaches, setCoaches] = useState(null)
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const isChatEnded = urlParams.get('chat_ended') === '1'
    const isCallMissed = urlParams.get('call_missed') === '1'

    if (isChatEnded || isCallMissed) {
      setIsInitialized(true)
      return
    }

    const handleConnected = () => setIsConnected(true)
    const handleCoachesUpdate = (updatedCoaches) => setCoaches(updatedCoaches)
    const handleDisconnected = () => setIsConnected(false)

    candidateChatManager.addEventListener('connected', handleConnected)
    candidateChatManager.addEventListener('coachesUpdate', handleCoachesUpdate)
    candidateChatManager.addEventListener('disconnected', handleDisconnected)

    if (socketUrl && username && chatRoom) {
      candidateChatManager
        .connect(socketUrl, username, chatRoom, {
          userMode: 'chat-list-on-candidate',
          socketUrl,
          username,
          chatRoom,
          inHours,
          logChatEventPath,
          chatCoachInfoPath,
          serverTime,
          currentUserLocale,
          displayName: userDisplayName || username,
          firstName: userFirstName,
          videoEnabled: videoEnabled || false,
          browser: browserName || 'unknown',
          userLocale: userLocale || 'en',
          userLanguage: userLanguage || 'English',
          userCompany,
          userCompanyId,
          partnerCandidate: partnerCandidate || false,
          conversationStarter,
          chatMode: chatMode || 'text',
          coachEasyRtcId: null,
          userId,
          userEmail,
          avatar,
          os,
          buildChatHistoryPath,
          conversationId,
          isMobile: isMobile || false,
          clientTime,
          timeOffset,
          deloitteUser,
          userCareerDevelopment,
          outplacement,
          opRedesign,
          enableAudio: false,
          enableVideo: false,
          enableDataChannels: false,
          autoInitUserMedia: false,
          debug: false,
          updateIntervalMs: 5000,
          appName: 'yns.coach_chat',
          presenceMessage: 'Coach Discovery',
          chatHours: chatHours || '',
          hideChatHours: hideChatHours || false,
          modalCopy: modalCopy || {},
        })
        .catch((error) => {
          console.error('CoachSelectionList - Failed to connect:', error)
        })
    }

    setIsInitialized(true)

    return () => {
      candidateChatManager.removeEventListener('connected', handleConnected)
      candidateChatManager.removeEventListener(
        'coachesUpdate',
        handleCoachesUpdate,
      )
      candidateChatManager.removeEventListener(
        'disconnected',
        handleDisconnected,
      )
    }
  }, [
    socketUrl,
    username,
    chatRoom,
    avatar,
    browserName,
    buildChatHistoryPath,
    chatMode,
    clientTime,
    conversationId,
    conversationStarter,
    currentUserLocale,
    deloitteUser,
    inHours,
    isMobile,
    logChatEventPath,
    chatCoachInfoPath,
    opRedesign,
    os,
    partnerCandidate,
    serverTime,
    timeOffset,
    userCareerDevelopment,
    outplacement,
    userCompany,
    userCompanyId,
    userDisplayName,
    userEmail,
    userFirstName,
    userId,
    userLanguage,
    userLocale,
    videoEnabled,
    chatHours,
    hideChatHours,
    modalCopy,
  ])

  const localeFiltered = getLocaleCompanyFilteredCoaches(coaches, {
    userLocale,
    userCompanyId,
    partnerCandidate,
  })
  const availableCoaches = applyCoachLoadBalancing(
    filterCoachesByStatus(localeFiltered, 'chat'),
  )
  const busyCoaches = filterCoachesByStatus(localeFiltered, 'dnd')

  return {
    isConnected,
    coaches,
    isInitialized,
    availableCoaches,
    busyCoaches,
  }
}
